import { getAuthHeader } from "@/services/authService";
import type { BlogPost } from "@/types/blogTypes";
import type { CraftIdea } from "@/components/tools/craft-generator/types";
import type { ElfScene } from "@/components/tools/scene-generator/types";
import type {
  LastName,
  NamePrefix,
  NameSuffix,
} from "@/components/tools/elf-name-generator/types";

type QueryResponse<T> = {
  data: T | null;
  error: Error | null;
  count?: number | null;
};

type QueryMode = "select" | "insert" | "update" | "delete";
type SingleMode = "none" | "single" | "maybeSingle";

type TableName =
  | "blog_posts"
  | "craft_ideas"
  | "holiday_recipes"
  | "elf_scenes"
  | "elf_name_prefixes"
  | "elf_name_suffixes"
  | "elf_last_names";

type RecipeRow = {
  id: string;
  title: string;
  description: string;
  ingredients: string[];
  instructions: string;
  prep_time: string;
  cook_time: string;
  total_time: string;
  servings: number;
  difficulty: string;
  category: string;
  tags: string[];
  image_url?: string;
  kid_friendly: boolean;
  created_at: string;
  updated_at: string;
};

type TableRowMap = {
  blog_posts: BlogPost;
  craft_ideas: CraftIdea;
  holiday_recipes: RecipeRow;
  elf_scenes: ElfScene;
  elf_name_prefixes: NamePrefix;
  elf_name_suffixes: NameSuffix;
  elf_last_names: LastName;
};

const API_BASE =
  typeof window === "undefined"
    ? ((globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env
        ?.SITE_URL ?? "https://elfletterbot.com")
    : "";

const NAME_TABLE_CONFIG = {
  elf_name_prefixes: {
    key: "prefixes",
    adminPath: "/api/admin/elf-names/prefixes",
  },
  elf_name_suffixes: {
    key: "suffixes",
    adminPath: "/api/admin/elf-names/suffixes",
  },
  elf_last_names: {
    key: "lastNames",
    adminPath: "/api/admin/elf-names/last-names",
  },
} as const;

async function requestJson<T>(
  path: string,
  init?: RequestInit,
  options?: { auth?: boolean },
): Promise<T> {
  const headers = new Headers(init?.headers);
  if (options?.auth) {
    Object.entries(getAuthHeader()).forEach(([key, value]) => headers.set(key, value));
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
  });

  const text = await response.text();
  const payload = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new Error(payload?.error || response.statusText || "Request failed");
  }

  return payload as T;
}

function applyFilters<T extends Record<string, any>>(
  rows: T[],
  filters: Array<{ field: string; value: unknown }>,
): T[] {
  return rows.filter((row) =>
    filters.every(({ field, value }) => {
      if (field === "published") {
        return Boolean(row[field]) === Boolean(value);
      }
      return row[field] === value;
    }),
  );
}

function applyOrdering<T extends Record<string, any>>(
  rows: T[],
  orderBy?: { column: string; ascending: boolean },
): T[] {
  if (!orderBy) return rows;

  return [...rows].sort((left, right) => {
    const leftValue = left[orderBy.column];
    const rightValue = right[orderBy.column];
    if (leftValue === rightValue) return 0;
    if (leftValue == null) return orderBy.ascending ? -1 : 1;
    if (rightValue == null) return orderBy.ascending ? 1 : -1;
    return leftValue > rightValue
      ? (orderBy.ascending ? 1 : -1)
      : (orderBy.ascending ? -1 : 1);
  });
}

class CloudflareTableBuilder<T extends Record<string, any> = Record<string, any>>
  implements PromiseLike<QueryResponse<T[]>>
{
  private readonly table: TableName;
  private mode: QueryMode = "select";
  private mutationPayload: any = null;
  private filters: Array<{ field: string; value: unknown }> = [];
  private orderBy?: { column: string; ascending: boolean };
  private rowLimit?: number;
  private wantsCount = false;
  private headOnly = false;

  constructor(table: TableName) {
    this.table = table;
  }

  select(_columns = "*", options?: { count?: string; head?: boolean }) {
    if (this.mode === "select") {
      this.wantsCount = options?.count === "exact";
      this.headOnly = Boolean(options?.head);
    }
    return this;
  }

  insert(payload: any | any[]) {
    this.mode = "insert";
    this.mutationPayload = payload;
    return this;
  }

  update(payload: any) {
    this.mode = "update";
    this.mutationPayload = payload;
    return this;
  }

  delete() {
    this.mode = "delete";
    return this;
  }

  eq(field: string, value: unknown) {
    this.filters.push({ field, value });
    return this;
  }

  order(column: string, options?: { ascending?: boolean }) {
    this.orderBy = {
      column,
      ascending: options?.ascending !== false,
    };
    return this;
  }

  limit(limit: number) {
    this.rowLimit = limit;
    return this;
  }

  single() {
    return this.execute("single");
  }

  maybeSingle() {
    return this.execute("maybeSingle");
  }

  then<TResult1 = QueryResponse<T[]>, TResult2 = never>(
    onfulfilled?:
      | ((value: QueryResponse<T[]>) => TResult1 | PromiseLike<TResult1>)
      | null,
    onrejected?:
      | ((reason: any) => TResult2 | PromiseLike<TResult2>)
      | null,
  ): Promise<TResult1 | TResult2> {
    return this.execute("none").then(onfulfilled ?? undefined, onrejected ?? undefined);
  }

  private getFilterValue(field: string) {
    return this.filters.find((entry) => entry.field === field)?.value;
  }

  private async fetchRows(): Promise<T[]> {
    switch (this.table) {
      case "blog_posts": {
        const slug = this.getFilterValue("slug");
        const id = this.getFilterValue("id");
        const published = this.getFilterValue("published");

        if (typeof slug === "string") {
          try {
            const post = await requestJson<T>(
              `/api/blog/${slug}`,
              undefined,
              { auth: false },
            );
            return post ? [post] : [];
          } catch {
            return [];
          }
        }

        if (typeof id === "string" || published === false) {
          const rows = await requestJson<T[]>("/api/admin/blog", undefined, { auth: true });
          return rows;
        }

        const publishedParam =
          typeof published === "boolean" ? String(published) : "true";
        return requestJson<T[]>(
          `/api/blog?published=${publishedParam}${this.rowLimit ? `&limit=${this.rowLimit}` : ""}`,
          undefined,
          { auth: false },
        );
      }
      case "craft_ideas":
        return requestJson<T[]>("/api/crafts");
      case "holiday_recipes":
        return requestJson<T[]>("/api/recipes");
      case "elf_scenes":
        return requestJson<T[]>("/api/elf-scenes?all=1");
      case "elf_name_prefixes":
      case "elf_name_suffixes":
      case "elf_last_names": {
        const response = await requestJson<{
          prefixes: T[];
          suffixes: T[];
          lastNames: T[];
        }>("/api/elf-names/components");

        return response[NAME_TABLE_CONFIG[this.table].key] as T[];
      }
      default:
        return [];
    }
  }

  private async performInsert(): Promise<T[]> {
    const rows = Array.isArray(this.mutationPayload) ? this.mutationPayload : [this.mutationPayload];

    switch (this.table) {
      case "blog_posts":
        return Promise.all(
          rows.map((row) =>
            requestJson<T>(
              "/api/admin/blog",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(row),
              },
              { auth: true },
            ),
          ),
        );
      case "craft_ideas":
        return Promise.all(
          rows.map((row) =>
            requestJson<T>(
              "/api/admin/crafts",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(row),
              },
              { auth: true },
            ),
          ),
        );
      case "holiday_recipes":
        return Promise.all(
          rows.map((row) =>
            requestJson<T>(
              "/api/admin/recipes",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(row),
              },
              { auth: true },
            ),
          ),
        );
      case "elf_scenes":
        return Promise.all(
          rows.map((row) =>
            requestJson<T>(
              "/api/admin/scenes",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(row),
              },
              { auth: true },
            ),
          ),
        );
      case "elf_name_prefixes":
      case "elf_name_suffixes":
      case "elf_last_names":
        return Promise.all(
          rows.map((row) =>
            requestJson<T>(
              NAME_TABLE_CONFIG[this.table].adminPath,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(row),
              },
              { auth: true },
            ),
          ),
        );
      default:
        return [];
    }
  }

  private async performUpdate(): Promise<T[]> {
    const id = this.getFilterValue("id");
    if (typeof id !== "string") {
      throw new Error("An id filter is required for updates");
    }

    switch (this.table) {
      case "blog_posts":
        return [
          await requestJson<T>(
            `/api/admin/blog/${id}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(this.mutationPayload),
            },
            { auth: true },
          ),
        ];
      case "craft_ideas":
        return [
          await requestJson<T>(
            `/api/admin/crafts/${id}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(this.mutationPayload),
            },
            { auth: true },
          ),
        ];
      case "holiday_recipes":
        return [
          await requestJson<T>(
            `/api/admin/recipes/${id}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(this.mutationPayload),
            },
            { auth: true },
          ),
        ];
      case "elf_scenes":
        return [
          await requestJson<T>(
            `/api/admin/scenes/${id}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(this.mutationPayload),
            },
            { auth: true },
          ),
        ];
      default:
        throw new Error(`Update is not supported for ${this.table}`);
    }
  }

  private async performDelete(): Promise<T[]> {
    const id = this.getFilterValue("id");
    if (typeof id !== "string") {
      throw new Error("An id filter is required for deletes");
    }

    switch (this.table) {
      case "blog_posts":
        await requestJson(
          `/api/admin/blog/${id}`,
          { method: "DELETE" },
          { auth: true },
        );
        return [];
      case "craft_ideas":
        await requestJson(
          `/api/admin/crafts/${id}`,
          { method: "DELETE" },
          { auth: true },
        );
        return [];
      case "holiday_recipes":
        await requestJson(
          `/api/admin/recipes/${id}`,
          { method: "DELETE" },
          { auth: true },
        );
        return [];
      case "elf_scenes":
        await requestJson(
          `/api/admin/scenes/${id}`,
          { method: "DELETE" },
          { auth: true },
        );
        return [];
      case "elf_name_prefixes":
      case "elf_name_suffixes":
      case "elf_last_names":
        await requestJson(
          `${NAME_TABLE_CONFIG[this.table].adminPath}/${id}`,
          { method: "DELETE" },
          { auth: true },
        );
        return [];
      default:
        return [];
    }
  }

  private async execute(singleMode: SingleMode): Promise<QueryResponse<any>> {
    try {
      let rows: T[] = [];

      if (this.mode === "select") {
        rows = await this.fetchRows();
        rows = applyFilters(rows, this.filters);
        rows = applyOrdering(rows, this.orderBy);
        if (typeof this.rowLimit === "number") {
          rows = rows.slice(0, this.rowLimit);
        }

        if (this.headOnly) {
          return {
            data: null,
            error: null,
            count: rows.length,
          };
        }
      } else if (this.mode === "insert") {
        rows = await this.performInsert();
      } else if (this.mode === "update") {
        rows = await this.performUpdate();
      } else if (this.mode === "delete") {
        rows = await this.performDelete();
      }

      if (singleMode !== "none") {
        const singleRow = rows[0] ?? null;
        if (!singleRow && singleMode === "single") {
          return { data: null, error: new Error("Row not found"), count: this.wantsCount ? rows.length : null };
        }

        return {
          data: singleRow,
          error: null,
          count: this.wantsCount ? rows.length : null,
        };
      }

      return {
        data: rows,
        error: null,
        count: this.wantsCount ? rows.length : null,
      };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unexpected request failure"),
        count: null,
      };
    }
  }
}

export const db = {
  from<K extends TableName>(table: K) {
    return new CloudflareTableBuilder<TableRowMap[K]>(table);
  },
};
